import type { CookieObj } from '@noths/polaris-client-gdpr-compliance';
import { acceptsTargetingAdvertisingCookies } from '@noths/polaris-client-gdpr-compliance';

import { MIRAKL_USER_ID_COOKIE } from './constants';
import type { MiraklConsent, MiraklRequestContext } from './types';

const NO_CONSENT: MiraklConsent = {
  optedIn: false,
  sendCounters: true,
  sendViews: false,
  userId: '',
};
const ROBOT: MiraklConsent = { optedIn: false, sendCounters: false, sendViews: false, userId: '' };

const toCookieObj = (cookies: MiraklRequestContext['cookies']): CookieObj =>
  Object.fromEntries(
    Object.entries(cookies).filter((entry): entry is [string, string] => entry[1] !== undefined),
  );

/**
 * Applies the Mirakl consent rules to one request. Every sender calls it, and so does the ad
 * request context sent to listings-api, so no call site re-implements the rules.
 *
 * | State     | Detected by                                   | `userId`  | `userKey`           | Views | Impressions and clicks |
 * | --------- | --------------------------------------------- | --------- | ------------------- | ----- | ---------------------- |
 * | Opted in  | `OptanonConsent` groups have `C0004:1`        | `mvt-uid` | the context's, if any | yes | yes                    |
 * | Opted in, no `mvt-uid` yet | as above, cookie absent      | `''`      | the context's, if any | no  | yes                    |
 * | Opted out | `OptanonConsent` present without `C0004:1`    | `''`      | absent              | no    | yes                    |
 * | Undecided | no `OptanonConsent` yet                       | `''`      | absent              | no    | yes                    |
 * | Robot     | `isBot`                                       | `''`      | absent              | no    | no                     |
 *
 * `C0004` is read through `acceptsTargetingAdvertisingCookies` from
 * `@noths/polaris-client-gdpr-compliance`. Impressions and clicks go out for opted-out and
 * undecided visitors because partners are billed on them; Mirakl counts them anonymously. Views
 * need a `userId`: Mirakl answers 400 to an empty one, so a visitor whose `mvt-uid` the edge worker
 * has not set yet, which is their first page, sends none.
 */
export const decideMiraklConsent = (context: MiraklRequestContext): MiraklConsent => {
  if (context.isBot) {
    return ROBOT;
  }

  if (!acceptsTargetingAdvertisingCookies(toCookieObj(context.cookies))) {
    return NO_CONSENT;
  }

  const userId = context.cookies[MIRAKL_USER_ID_COOKIE] ?? '';

  return {
    optedIn: true,
    sendCounters: true,
    sendViews: userId !== '',
    userId,
    ...(context.userKey ? { userKey: context.userKey } : {}),
  };
};
